import { useCallback, useEffect, useMemo, useState } from "react";
import type { AppLanguage } from "../app/language";
import type { ArchiveItemDetail, ArchiveItemSummary } from "../app/types";

type ArchiveViewProps = {
  language: AppLanguage;
  canEditArchive: boolean;
  onLoadArchiveItems: (filters?: { query?: string; itemKind?: string; archiveKind?: string; limit?: number }) => Promise<ArchiveItemSummary[]>;
  onLoadArchiveItemDetail: (itemId: string) => Promise<ArchiveItemDetail>;
  onDeleteArchiveItem: (itemId: string) => Promise<{ ok: boolean; deletedItemId: string; deletedArchiveId: string; deletedArchive: boolean; hasRemainingArchiveData: boolean }>;
};

const copy = {
  en: {
    eyebrow: "Archive management",
    title: "Archive",
    description: "Search, inspect, download, and delete overflow records stored in the workspace archive.",
    searchLabel: "Search archived data",
    searchPlaceholder: "Search ticket subject, record id, or archive text",
    archiveKindLabel: "Type",
    searchButton: "Search archive",
    refreshButton: "Refresh",
    loading: "Loading archive items…",
    loadingDetail: "Loading archive detail…",
    empty: "No archived records match the current filters.",
    detailTitle: "Archive detail",
    detailDescription: "Select an archived record to inspect the stored payload and its archive batch.",
    deleteButton: "Delete archived record",
    downloadButton: "Download batch JSON",
    confirmDelete: "Delete this archived record permanently?",
    deleted: "Archived record deleted.",
    deletedBatch: "Archived record deleted. The archive batch is now empty and was removed.",
    results: "Results",
    archivedAt: "Archived",
    recordTime: "Record time",
    archiveBatch: "Archive batch",
    recordId: "Record id",
    usageCounts: "Usage counts",
    payload: "Stored payload",
    batchInfo: "Batch info",
    tickets: "Tickets",
    knowledgeViews: "Knowledge views",
    auditEvents: "Audit events",
    queryErrorFallback: "Unable to load archive data.",
    batchAll: "All types",
  },
  ja: {
    eyebrow: "アーカイブ管理",
    title: "アーカイブ",
    description: "ワークスペースアーカイブに保存されたオーバーフローレコードを検索、確認、ダウンロード、削除します。",
    searchLabel: "アーカイブを検索",
    searchPlaceholder: "チケット件名、レコード ID、アーカイブ本文で検索",
    archiveKindLabel: "種別",
    searchButton: "アーカイブを検索",
    refreshButton: "再読み込み",
    loading: "アーカイブ項目を読み込んでいます…",
    loadingDetail: "アーカイブ詳細を読み込んでいます…",
    empty: "条件に一致するアーカイブ項目はありません。",
    detailTitle: "アーカイブ詳細",
    detailDescription: "保存済みペイロードと所属バッチを確認するには、アーカイブ項目を選択してください。",
    deleteButton: "アーカイブ項目を削除",
    downloadButton: "バッチ JSON をダウンロード",
    confirmDelete: "このアーカイブ項目を完全に削除しますか？",
    deleted: "アーカイブ項目を削除しました。",
    deletedBatch: "アーカイブ項目を削除しました。バッチが空になったため削除されました。",
    results: "検索結果",
    archivedAt: "保存日時",
    recordTime: "レコード時刻",
    archiveBatch: "アーカイブバッチ",
    recordId: "レコード ID",
    usageCounts: "使用量カウント",
    payload: "保存済みペイロード",
    batchInfo: "バッチ情報",
    tickets: "チケット",
    knowledgeViews: "ナレッジ閲覧",
    auditEvents: "監査イベント",
    queryErrorFallback: "アーカイブデータを読み込めませんでした。",
    batchAll: "すべての種別",
  },
} as const;

const archiveKindOptions = [
  { value: "", en: "All types", ja: "すべての種別" },
  { value: "resolved-tickets", en: "Resolved tickets", ja: "解決済みチケット" },
  { value: "knowledge-article-views", en: "Knowledge article views", ja: "ナレッジ記事閲覧" },
] as const;

function inputClassName() {
  return "w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary-ring)]";
}

function formatTimestamp(value: string | null | undefined, language: AppLanguage) {
  if (!value) {
    return "—";
  }

  const timestamp = Date.parse(value);

  if (!Number.isFinite(timestamp)) {
    return value;
  }

  return new Date(timestamp).toLocaleString(language === "ja" ? "ja-JP" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatNumber(value: number) {
  return new Intl.NumberFormat().format(value);
}

function formatBytes(value: number) {
  if (!Number.isFinite(value) || value <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
}

function getArchiveKindLabel(value: string, language: AppLanguage) {
  const option = archiveKindOptions.find(entry => entry.value === value);

  if (option) {
    return option[language];
  }

  return value;
}

function LoadingSpinnerBadge({ label }: { label: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[var(--brand-primary-ring)] bg-white/95 px-3 py-2 text-xs font-semibold text-[var(--brand-primary)] shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span>{label}</span>
    </div>
  );
}

function CountCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{label}</p>
      <p className="mt-4 text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{value}</p>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

export function ArchiveView({
  language,
  canEditArchive,
  onLoadArchiveItems,
  onLoadArchiveItemDetail,
  onDeleteArchiveItem,
}: ArchiveViewProps) {
  const localized = copy[language];
  const [draftQuery, setDraftQuery] = useState("");
  const [query, setQuery] = useState("");
  const [archiveKind, setArchiveKind] = useState("");
  const [items, setItems] = useState<ArchiveItemSummary[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedItemDetail, setSelectedItemDetail] = useState<ArchiveItemDetail | null>(null);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const loadItems = useCallback(async () => {
    setIsLoadingItems(true);
    setError(null);

    try {
      const nextItems = await onLoadArchiveItems({
        query,
        archiveKind,
        limit: 100,
      });

      setItems(nextItems);
      setSelectedItemId(currentSelectedItemId => nextItems.some(item => item.id === currentSelectedItemId)
        ? currentSelectedItemId
        : (nextItems[0]?.id ?? null));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : localized.queryErrorFallback);
      setItems([]);
      setSelectedItemId(null);
    } finally {
      setIsLoadingItems(false);
    }
  }, [archiveKind, localized.queryErrorFallback, onLoadArchiveItems, query]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!selectedItemId) {
      setSelectedItemDetail(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      setIsLoadingDetail(true);

      try {
        const detail = await onLoadArchiveItemDetail(selectedItemId);

        if (!cancelled) {
          setSelectedItemDetail(detail);
        }
      } catch (detailError) {
        if (!cancelled) {
          setError(detailError instanceof Error ? detailError.message : localized.queryErrorFallback);
          setSelectedItemDetail(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingDetail(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [localized.queryErrorFallback, onLoadArchiveItemDetail, selectedItemId]);

  const totals = useMemo(() => {
    return items.reduce((summary, item) => {
      if (item.itemKind === "ticket") {
        summary.ticketCount += 1;
      }

      if (item.itemKind === "knowledge-view") {
        summary.knowledgeViewCount += 1;
      }

      if (item.itemKind === "audit-event") {
        summary.auditEventCount += 1;
      }

      return summary;
    }, {
      ticketCount: 0,
      knowledgeViewCount: 0,
      auditEventCount: 0,
    });
  }, [items]);

  const handleSearchSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatusMessage(null);
    setQuery(draftQuery.trim());
  };

  const handleDelete = async () => {
    if (!canEditArchive || !selectedItemDetail || isDeleting || typeof window === "undefined") {
      return;
    }

    if (!window.confirm(localized.confirmDelete)) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const result = await onDeleteArchiveItem(selectedItemDetail.item.id);
      setStatusMessage(result.deletedArchive ? localized.deletedBatch : localized.deleted);
      setSelectedItemDetail(null);
      await loadItems();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : localized.queryErrorFallback);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <section className="space-y-8 p-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{localized.eyebrow}</p>
          <div>
            <h1 className="font-headline text-4xl font-extrabold tracking-[-0.06em] text-[#101828]">{localized.title}</h1>
            <p className="mt-3 max-w-3xl text-sm text-[#667085]">{localized.description}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setStatusMessage(null);
            void loadItems();
          }}
          className="rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-semibold text-[#344054] transition hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
        >
          {localized.refreshButton}
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <CountCard label={localized.results} value={formatNumber(items.length)} detail={localized.description} />
        <CountCard label={localized.tickets} value={formatNumber(totals.ticketCount)} detail={getArchiveKindLabel("resolved-tickets", language)} />
        <CountCard label={localized.auditEvents} value={formatNumber(totals.auditEventCount + totals.knowledgeViewCount)} detail={`${formatNumber(totals.knowledgeViewCount)} ${localized.knowledgeViews} · ${formatNumber(totals.auditEventCount)} ${localized.auditEvents}`} />
      </div>

      <form onSubmit={handleSearchSubmit} className="rounded-[28px] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]">
          <label className="space-y-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{localized.searchLabel}</span>
            <input
              value={draftQuery}
              onChange={event => setDraftQuery(event.target.value)}
              placeholder={localized.searchPlaceholder}
              className={inputClassName()}
            />
          </label>

          <label className="space-y-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{localized.archiveKindLabel}</span>
            <select value={archiveKind} onChange={event => setArchiveKind(event.target.value)} className={inputClassName()}>
              {archiveKindOptions.map(option => <option key={option.value || "all"} value={option.value}>{option[language]}</option>)}
            </select>
          </label>

          <div className="flex items-end">
            <button type="submit" className="brand-primary-button w-full rounded-2xl px-5 py-3 text-sm font-bold text-white transition-all">
              {localized.searchButton}
            </button>
          </div>
        </div>
      </form>

      {statusMessage ? <div className="rounded-2xl border border-[#b7ebd3] bg-[#ecfdf3] px-4 py-3 text-sm font-semibold text-[#027a48]">{statusMessage}</div> : null}
      {error ? <div className="rounded-2xl border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm font-semibold text-[#b42318]">{error}</div> : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.25fr)]">
        <div className="rounded-[28px] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{localized.results}</p>
              <h2 className="mt-2 text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{formatNumber(items.length)}</h2>
            </div>
          </div>

          <div className="space-y-3">
            {isLoadingItems && items.length === 0 ? (
              <div className="flex justify-center rounded-3xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-5 py-8">
                <LoadingSpinnerBadge label={localized.loading} />
              </div>
            ) : null}

            {!isLoadingItems && items.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-5 py-8 text-sm text-[#667085]">{localized.empty}</div>
            ) : null}

            {items.map(item => {
              const isSelected = item.id === selectedItemId;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setStatusMessage(null);
                    setSelectedItemId(item.id);
                  }}
                  className={`w-full rounded-[24px] border px-5 py-4 text-left transition ${isSelected
                    ? "border-[var(--brand-primary)] bg-[var(--brand-primary-soft)] shadow-[0_18px_40px_rgba(0,105,73,0.12)]"
                    : "border-[#eaecf0] bg-white hover:border-[var(--brand-primary)]/50 hover:bg-[#f8fffb]"}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#ecfdf3] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#027a48]">{getArchiveKindLabel(item.archiveKind, language)}</span>
                      </div>
                      <p className="mt-3 truncate text-base font-bold text-[#101828]">{item.title || item.recordId}</p>
                      <p className="mt-1 text-sm text-[#667085]">{item.subtitle || item.recordId}</p>
                    </div>
                    <span className="text-xs font-medium text-[#667085]">{formatTimestamp(item.recordTimestamp ?? item.createdAt, language)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-[28px] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{localized.detailTitle}</p>
              <h2 className="mt-2 text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{selectedItemDetail?.item.title || localized.detailTitle}</h2>
              <p className="mt-2 max-w-2xl text-sm text-[#667085]">{selectedItemDetail?.item.subtitle || localized.detailDescription}</p>
            </div>

            {selectedItemDetail ? (
              <div className="flex flex-wrap gap-3">
                <a
                  href={`/api/settings/database/overflow/archives/${encodeURIComponent(selectedItemDetail.archive.id)}`}
                  className="rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-semibold text-[#344054] transition hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
                >
                  {localized.downloadButton}
                </a>
                {canEditArchive ? (
                  <button
                    type="button"
                    onClick={() => {
                      void handleDelete();
                    }}
                    disabled={isDeleting}
                    className="rounded-2xl bg-[#b42318] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#912018] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {localized.deleteButton}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          {!selectedItemId ? (
            <div className="rounded-3xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-5 py-8 text-sm text-[#667085]">{localized.detailDescription}</div>
          ) : isLoadingDetail ? (
            <div className="rounded-3xl border border-[#d0d5dd] bg-[#f8fafc] px-5 py-8 text-sm font-semibold text-[var(--brand-primary)]">{localized.loadingDetail}</div>
          ) : selectedItemDetail ? (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-[24px] border border-[#eaecf0] bg-[#fcfcfd] p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{localized.recordId}</p>
                  <p className="mt-3 break-all text-sm font-semibold text-[#101828]">{selectedItemDetail.item.recordId}</p>
                  <div className="mt-4 space-y-2 text-sm text-[#667085]">
                    <p><span className="font-semibold text-[#344054]">{localized.recordTime}:</span> {formatTimestamp(selectedItemDetail.item.recordTimestamp, language)}</p>
                    <p><span className="font-semibold text-[#344054]">{localized.archivedAt}:</span> {formatTimestamp(selectedItemDetail.item.createdAt, language)}</p>
                    <p><span className="font-semibold text-[#344054]">{localized.archiveBatch}:</span> {selectedItemDetail.archive.id}</p>
                  </div>
                </div>

                <div className="rounded-[24px] border border-[#eaecf0] bg-[#fcfcfd] p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{localized.usageCounts}</p>
                  <div className="mt-4 space-y-2 text-sm text-[#667085]">
                    <p><span className="font-semibold text-[#344054]">{localized.tickets}:</span> {formatNumber(selectedItemDetail.item.usageCounts.ticketCount)}</p>
                    <p><span className="font-semibold text-[#344054]">Articles:</span> {formatNumber(selectedItemDetail.item.usageCounts.articleCount)}</p>
                    <p><span className="font-semibold text-[#344054]">Storage:</span> {formatBytes(selectedItemDetail.item.usageCounts.storageBytes)}</p>
                    <p><span className="font-semibold text-[#344054]">Objects:</span> {formatNumber(selectedItemDetail.item.usageCounts.storageObjectCount)}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-[24px] border border-[#eaecf0] bg-[#fcfcfd] p-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{localized.batchInfo}</p>
                <div className="mt-4 grid gap-3 md:grid-cols-2 text-sm text-[#667085]">
                  <p><span className="font-semibold text-[#344054]">Kind:</span> {getArchiveKindLabel(selectedItemDetail.archive.archiveKind, language)}</p>
                  <p><span className="font-semibold text-[#344054]">Rows:</span> {formatNumber(selectedItemDetail.archive.rowCount)}</p>
                  <p><span className="font-semibold text-[#344054]">Payload:</span> {formatBytes(selectedItemDetail.archive.payloadBytes)}</p>
                  <p><span className="font-semibold text-[#344054]">Created:</span> {formatTimestamp(selectedItemDetail.archive.createdAt, language)}</p>
                </div>
              </div>

              <div className="rounded-[24px] border border-[#eaecf0] bg-[#0f172a] p-5 text-white shadow-[0_20px_60px_rgba(15,23,42,0.18)]">
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#94a3b8]">{localized.payload}</p>
                <pre className="mt-4 max-h-[520px] overflow-auto whitespace-pre-wrap break-words text-sm leading-6 text-[#e2e8f0]">{JSON.stringify(selectedItemDetail.record, null, 2)}</pre>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-5 py-8 text-sm text-[#667085]">{localized.detailDescription}</div>
          )}
        </div>
      </div>
    </section>
  );
}